import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { HOLD_CAUSES, LABELS } from "@match/shared";
import { PageHeader } from "@/components/Layout";
import { DataTable, type DataTableColumn } from "@/components/table";
import { HoldButton, PHASE_LABEL, ResetDoneButton } from "@/components/session";
import { ErrorNote, MoreMenu, useCanWrite } from "@/components/ui";
import { TogliAttesa } from "@/pages/ClientDetail";
import { api, errorMessage } from "@/lib/api";
import { dateTime } from "@/lib/format";
import type { OnHoldSession } from "@/lib/types";

/*
 * FERMI: le Fasi ferme del tenant, dalla piu' vecchia, filtrabili per causa.
 *
 * La causa e' un VALORE (`cause`, dal backend), non una colonna fissa: i
 * pulsanti si costruiscono dalle cause note piu' quelle che arrivano, cosi'
 * un fermo nuovo (l'attesa delle credenziali) si conta senza toccare la
 * pagina. Le stesse Fasi restano visibili in «Fasi» e nella scheda.
 *
 * Accanto alle pause ci sono le ATTESE DELLE CREDENZIALI (21/10): Fasi chiuse
 * — bruciate o superate — che aspettano il conto prop nuovo. La riga e' la
 * Fase chiusa; al posto di «Riprendi» c'e' «Nuovo conto», che apre il pannello
 * della scheda del cliente (sa quali suoi broker sono liberi).
 *
 * La chiave sta sotto `["sessions"]`: «Riprendi» rinfresca le Fasi
 * (`useRefreshDirectory`) e con loro questa lista.
 */

const TUTTE = "TUTTE";

/** Come si legge una causa: quelle note hanno la loro etichetta, una sconosciuta il suo valore. */
function causeLabel(cause: string): string {
  return (LABELS.holdCause as Record<string, string>)[cause] ?? cause;
}

/** Il parametro con cui la scheda del cliente apre «Nuovo conto» su questa attesa. */
const attesaParam = (a: NonNullable<OnHoldSession["awaiting"]>) => (a.cycleId ? `ciclo:${a.cycleId}` : `fase:${a.sessionId}`);

export function Fermi() {
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const fermi = useQuery({ queryKey: ["sessions", "on-hold"], queryFn: () => api.sessions.onHold() });
  const [causa, setCausa] = useState<string>(TUTTE);

  const all = useMemo(() => fermi.data ?? [], [fermi.data]);

  const opzioni = useMemo(() => {
    const conteggi = new Map<string, number>();
    for (const s of all) conteggi.set(s.cause, (conteggi.get(s.cause) ?? 0) + 1);
    const cause: string[] = [...HOLD_CAUSES, ...[...conteggi.keys()].filter((c) => !(HOLD_CAUSES as readonly string[]).includes(c))];
    return [
      { key: TUTTE, label: "Tutte", count: all.length },
      ...cause.map((c) => ({ key: c, label: causeLabel(c), count: conteggi.get(c) ?? 0 })),
    ];
  }, [all]);

  const filtered = useMemo(() => (causa === TUTTE ? all : all.filter((s) => s.cause === causa)), [all, causa]);

  const columns: DataTableColumn<OnHoldSession>[] = [
    {
      key: "client",
      header: "Cliente",
      cell: (s) => <span className="font-medium text-ink">{s.client.name}</span>,
      sortBy: (s) => s.client.name.toLowerCase(),
    },
    {
      key: "prop",
      header: "Conto prop",
      cell: (s) =>
        s.awaiting ? (
          // In attesa: il lato prop e' vuoto, e sotto c'e' che cosa era.
          <span className="text-faint">
            — <span className="block text-xs">era {[PHASE_LABEL(s.phase), s.presetName, s.propAccount.mt5Login].filter(Boolean).join(" · ")}</span>
          </span>
        ) : (
          <span className="text-prop">
            {s.propAccount.label} <span className="machine-id text-xs text-faint">{s.propAccount.mt5Login}</span>
          </span>
        ),
      sortBy: (s) => s.propAccount.label.toLowerCase(),
    },
    {
      key: "broker",
      header: "Conto broker",
      cell: (s) =>
        s.brokerAccount ? (
          <span className="text-broker">
            {s.brokerAccount.label} <span className="machine-id text-xs text-faint">{s.brokerAccount.mt5Login}</span>
          </span>
        ) : (
          <span className="text-faint">—</span>
        ),
      sortBy: (s) => (s.brokerAccount?.label ?? "").toLowerCase(),
    },
    { key: "phase", header: "Fase", cell: (s) => PHASE_LABEL(s.phase), sortBy: (s) => s.phase },
    {
      key: "firm",
      header: "Prop firm",
      cell: (s) => s.propFirm?.name ?? <span className="text-faint">—</span>,
      sortBy: (s) => (s.propFirm?.name ?? "").toLowerCase(),
    },
    {
      key: "cause",
      header: "Causa",
      // Il testo del motivo salvato, non quello del filtro: una pausa vecchia si legge com'era.
      cell: (s) => (
        <span className="text-warn">{s.awaiting ? s.holdText : s.holdReason ? LABELS.holdReason[s.holdReason] : "motivo non registrato"}</span>
      ),
      sortBy: (s) => s.cause,
    },
    {
      key: "note",
      header: "Nota",
      cell: (s) => (s.holdNote ? <span title={s.holdNote}>{s.holdNote}</span> : <span className="text-faint">—</span>),
    },
    {
      key: "since",
      header: "Ferma da",
      cell: (s) => (s.holdSince ? dateTime(s.holdSince) : <span className="text-faint">non registrato</span>),
      sortBy: (s) => s.holdSince,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (s) => (
        // stopPropagation: la riga apre la Fase, i pulsanti non devono farlo scattare due volte.
        <span className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          <Link to={`/fasi/${s.id}`} className="rounded px-2 py-1 text-xs font-medium text-muted hover:bg-raised hover:text-ink">
            Apri la Fase
          </Link>
          {canWrite &&
            (s.awaiting ? (
              <>
                <Link
                  to={`/clienti/${s.client.id}?attesa=${attesaParam(s.awaiting)}`}
                  className="rounded px-2 py-1 text-xs font-medium text-ink hover:bg-raised"
                >
                  Nuovo conto
                </Link>
                {/* Sulla Fase si toglie l'attesa; sul ciclo il ciclo si chiude come abbandonato. */}
                <MoreMenu>
                  {s.awaiting.cycleId ? <TogliAttesa cycleId={s.awaiting.cycleId} /> : <TogliAttesa sessionId={s.awaiting.sessionId!} />}
                </MoreMenu>
              </>
            ) : (
              <>
                {s.holdReason === "RESET" && <ResetDoneButton sessionId={s.id} size="sm" />}
                <HoldButton session={{ id: s.id, state: "PAUSED" }} size="sm" />
              </>
            ))}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Fermi" subtitle="Le Fasi ferme e quelle in attesa delle credenziali, dalla più vecchia: qui non si apre niente" />

      <div className="space-y-4 px-6 py-5">
        <div role="group" aria-label="Causa del fermo" className="flex w-fit flex-wrap gap-1 rounded border border-line bg-raised p-1">
          {opzioni.map((o) => (
            <button
              key={o.key}
              type="button"
              aria-pressed={causa === o.key}
              onClick={() => setCausa(o.key)}
              className={[
                "rounded px-2.5 py-1 text-xs font-medium transition-colors",
                causa === o.key ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink",
              ].join(" ")}
            >
              {o.label} <span className="tabular-nums text-faint">{o.count}</span>
            </button>
          ))}
        </div>

        {fermi.isLoading ? (
          <p className="text-sm text-muted">Caricamento…</p>
        ) : fermi.error ? (
          <ErrorNote>{errorMessage(fermi.error)}</ErrorNote>
        ) : (
          <div className="card">
            <DataTable
              columns={columns}
              rows={filtered}
              rowKey={(s) => `${s.awaiting ? "attesa" : "pausa"}-${s.id}`}
              onRowClick={(s) => navigate(`/fasi/${s.id}`)}
              empty={
                all.length === 0
                  ? {
                      title: "Nessuna Fase ferma",
                      hint: "Una Fase si ferma dalla sua scheda o dall'elenco Fasi, col pulsante «Pausa» e il motivo. Qui compaiono anche le Fasi chiuse in attesa delle credenziali nuove.",
                    }
                  : { title: "Nessuna Fase ferma per questa causa", hint: "Scegli un'altra causa o «Tutte»." }
              }
            />
          </div>
        )}
      </div>
    </>
  );
}
